// WebAudio SFX. Gunshots + body hits are real recordings (CC0 — client/sfx/LICENSE.txt),
// fetched once per session (the device HTTP-caches the files) and played
// POSITIONALLY through a panner (HRTF on desktop, equalpower on standalone
// headsets — see PAN_MODEL): the listener follows the headset, so
// every shot — yours, other players', bots' — arrives from its true world
// direction, delayed by the speed of sound and dulled by distance.
// Everything else (wind, engine, jet, water, UI beeps) stays synthesized.
'use strict';

let ctx = null;
let windGain = null, windFilter = null;
let masterLP = null;   // master bus lowpass — the underwater muffle (Sfx.duck)

// EVERY source routes through this one lowpass so Sfx.duck can dull the whole
// mix at once when the eye dips under a water sheet. 19 kHz = transparent.
// (Voice chat hangs its panners off the same context but connects directly —
// remote voices stay clear; they are above the water.)
function dest(c) {
  if (!masterLP) {
    masterLP = c.createBiquadFilter();
    masterLP.type = 'lowpass';
    masterLP.frequency.value = 19000;
    masterLP.connect(c.destination);   // the ONE direct destination hookup
  }
  return masterLP;
}

// ---- sample playback ------------------------------------------------------
const samples = {};            // name -> decoded AudioBuffer (session cache)
let samplesRequested = false;
function loadSamples() {
  if (samplesRequested) return;
  samplesRequested = true;
  const c = ac();
  for (const name of ['shot_rifle', 'shot_sniper', 'hit_body']) {
    fetch(`sfx/${name}.ogg`)
      .then((r) => { if (!r.ok) throw new Error('http ' + r.status); return r.arrayBuffer(); })
      .then((b) => new Promise((res, rej) => c.decodeAudioData(b, res, rej)))
      .then((buf) => { samples[name] = buf; })
      .catch((e) => console.warn('[sfx] sample load failed:', name, e));
  }
}

// HRTF convolution is desktop-only: on standalone headsets (Quest) and phones
// Chromium's HRTF path is heavy and unreliably near-silent — panned shots
// vanished on Quest while the direct-connected loops played fine. equalpower
// keeps the head-tracked L/R pan and the same distance rolloff via the cheap
// path. Decided once at load; voice.js panners use the same model.
const PAN_MODEL =
  /OculusBrowser|Quest|Pico|Wolvic|VRBrowser|Android|Mobile/i.test(navigator.userAgent)
    ? 'equalpower' : 'HRTF';

function makePanner(x, y, z) {
  const c = ac();
  const p = c.createPanner();
  p.panningModel = PAN_MODEL;
  p.distanceModel = 'inverse';
  p.refDistance = 12;
  p.rolloffFactor = 0.7;
  p.maxDistance = 4000;
  if (p.positionX) { p.positionX.value = x; p.positionY.value = y; p.positionZ.value = z; }
  else p.setPosition(x, y, z);
  return p;
}

// pos {x,y,z} world-space → HRTF-panned; omitted → plain head-locked playback
function playSample(name, gain, pos, opts = {}) {
  const buf = samples[name];
  if (!buf) return false;
  const c = ac();
  const src = c.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = opts.rate || 1;
  const g = c.createGain();
  g.gain.value = gain;
  let head = src;
  if (opts.lowpass && opts.lowpass < 18000) {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = opts.lowpass;
    src.connect(f);
    head = f;
  }
  head.connect(g);
  if (pos) g.connect(makePanner(pos.x, pos.y, pos.z)).connect(dest(c));
  else g.connect(dest(c));
  src.start(c.currentTime + (opts.delay || 0));
  return true;
}

function ac() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    // wind loop: filtered noise, gain driven by speed
    const len = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    windFilter = ctx.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 300;
    windFilter.Q.value = 0.6;
    windGain = ctx.createGain();
    windGain.gain.value = 0;
    src.connect(windFilter).connect(windGain).connect(dest(ctx));
    src.start();
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// noiseBurst, but HRTF-panned at a world position (with optional start delay)
function burstAt(pos, dur, freq, q, gain, type, delay) {
  const c = ac();
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(f).connect(g).connect(makePanner(pos.x, pos.y, pos.z)).connect(dest(c));
  src.start(c.currentTime + (delay || 0));
}

function noiseBurst(dur, freq, q, gain, type = 'lowpass') {
  const c = ac();
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = c.createBufferSource();
  src.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  src.connect(f).connect(g).connect(dest(c));
  src.start();
}

// persistent loops for the two vehicles — created lazily, gain 0 when idle
let engine = null;   // bike: two detuned oscillators through a lowpass
let jetN = null;     // jet-suit: broadband noise through a bandpass
let liftN = null;    // chairlift: detuned motor beat + cable whir
let swimN = null;    // swimming: lowpassed noise lapping under a slow LFO

function swimNodes() {
  const c = ac();
  if (!swimN) {
    const len = c.sampleRate * 2;
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 520;
    f.Q.value = 0.9;
    // a ~0.9 Hz sine wobbling the loop gain turns steady hiss into LAPPING —
    // depth rides the same level as the base gain (Sfx.swim)
    const g = c.createGain();
    g.gain.value = 0;
    const lfo = c.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.9;
    const lfoG = c.createGain();
    lfoG.gain.value = 0;
    lfo.connect(lfoG).connect(g.gain);
    src.connect(f).connect(g).connect(dest(c));
    src.start();
    lfo.start();
    swimN = { f, g, lfoG };
  }
  return swimN;
}

function engineNodes() {
  const c = ac();
  if (!engine) {
    const o1 = c.createOscillator();
    o1.type = 'sawtooth';
    const o2 = c.createOscillator();
    o2.type = 'square';
    o2.detune.value = 9;
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 400;
    f.Q.value = 1.6;
    const g = c.createGain();
    g.gain.value = 0;
    o1.connect(f);
    o2.connect(f);
    f.connect(g).connect(dest(c));
    o1.start();
    o2.start();
    engine = { o1, o2, f, g };
  }
  return engine;
}

function jetNodes() {
  const c = ac();
  if (!jetN) {
    const len = c.sampleRate * 2;
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 900;
    f.Q.value = 0.7;
    const g = c.createGain();
    g.gain.value = 0;
    src.connect(f).connect(g).connect(dest(c));
    src.start();
    jetN = { f, g };
  }
  return jetN;
}

function liftNodes() {
  const c = ac();
  if (!liftN) {
    // two barely-detuned sines beat at 3 Hz — the slow drive-motor throb —
    // plus quiet bandpassed noise for the cable whir over the sheaves
    const o1 = c.createOscillator();
    o1.type = 'sine';
    o1.frequency.value = 60;
    const o2 = c.createOscillator();
    o2.type = 'sine';
    o2.frequency.value = 63;
    const len = c.sampleRate * 2;
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 320;
    f.Q.value = 1.1;
    const ng = c.createGain();
    ng.gain.value = 0.35;
    const g = c.createGain();
    g.gain.value = 0;
    o1.connect(g);
    o2.connect(g);
    src.connect(f).connect(ng).connect(g);
    g.connect(dest(c));
    o1.start();
    o2.start();
    src.start();
    liftN = { g };
  }
  return liftN;
}

function tone(freq, dur, gain, type = 'sine', slide = 0) {
  const c = ac();
  const o = c.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, c.currentTime);
  if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq + slide), c.currentTime + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  o.connect(g).connect(dest(c));
  o.start();
  o.stop(c.currentTime + dur);
}

export const Sfx = {
  unlock() { ac(); loadSamples(); },
  // shared AudioContext accessor: voice panners (client/src/voice.js) MUST hang
  // off this context or the head-tracked listener below won't spatialize them
  ctx() { return ac(); },
  // the platform-appropriate PannerNode model (see PAN_MODEL) — voice.js reads it
  panModel() { return PAN_MODEL; },
  // per-frame: point the WebAudio listener at the active camera's world pose
  // so panned sources land in the right ear. cam is a THREE camera; we read
  // its matrixWorld directly (pos = translation, fwd = -Z col, up = +Y col).
  listener(cam) {
    if (!ctx) return;
    const l = ctx.listener;
    const e = cam.matrixWorld.elements;
    if (l.positionX) {
      l.positionX.value = e[12]; l.positionY.value = e[13]; l.positionZ.value = e[14];
      l.forwardX.value = -e[8]; l.forwardY.value = -e[9]; l.forwardZ.value = -e[10];
      l.upX.value = e[4]; l.upY.value = e[5]; l.upZ.value = e[6];
    } else {
      l.setPosition(e[12], e[13], e[14]);
      l.setOrientation(-e[8], -e[9], -e[10], e[4], e[5], e[6]);
    }
  },
  // own shot — real sample at the muzzle's world position (samples still
  // downloading on the very first shots → synth fallback)
  shot(weapon, pos) {
    const name = weapon === 'sniper' ? 'shot_sniper' : 'shot_rifle';
    const gain = weapon === 'sniper' ? 0.9 : 0.7;
    if (playSample(name, gain, pos, { rate: 0.95 + Math.random() * 0.1 })) return;
    if (weapon === 'sniper') {
      noiseBurst(0.35, 900, 0.4, 0.55);
      tone(120, 0.28, 0.35, 'triangle', -70);
    } else {
      noiseBurst(0.14, 1400, 0.5, 0.4);
      tone(180, 0.1, 0.22, 'square', -90);
    }
  },
  // another player's / bot's muzzle report: HRTF-panned at the shooter,
  // arriving at the speed of sound, air-absorbed (lowpass) with distance
  shotAt(weapon, pos, dist) {
    const name = weapon === 'sniper' ? 'shot_sniper' : 'shot_rifle';
    if (!playSample(name, weapon === 'sniper' ? 1.0 : 0.8, pos, {
      rate: 0.95 + Math.random() * 0.1,
      delay: (dist || 0) / 343,
      lowpass: Math.max(1200, 18000 - (dist || 0) * 14),
    })) noiseBurst(0.25, 500, 0.4, 0.12);
  },
  // bullet strike at pos — terrain, a building wall or a structure (the
  // server's impact event says where). The sniper round gets its own bigger
  // voice: a deep slam wrapped in a long air hiss; the rifle keeps the short
  // dirt/rock puff. dist adds the speed-of-sound delay.
  impact(pos, weapon, dist) {
    const sniper = weapon === 'sniper';
    if (pos && ctx) {
      const delay = (dist || 0) / 343;
      if (sniper) {
        burstAt(pos, 0.38, 300, 0.7, 0.95, 'lowpass', delay);    // deep slam
        burstAt(pos, 0.55, 5200, 0.5, 0.4, 'highpass', delay);   // air hiss
        burstAt(pos, 0.12, 1800, 0.8, 0.5, 'bandpass', delay);   // debris crack
      } else if (weapon === 'laser') {
        // bot energy round landing: sizzle + a falling zap blip, no dirt
        burstAt(pos, 0.16, 4200, 2.0, 0.3, 'bandpass', delay);
        const c = ac();
        const t0 = c.currentTime + delay;
        const o = c.createOscillator();
        o.type = 'sawtooth';
        o.frequency.setValueAtTime(900, t0);
        o.frequency.exponentialRampToValueAtTime(140, t0 + 0.1);
        const g = c.createGain();
        g.gain.setValueAtTime(0.25, t0);
        g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.12);
        o.connect(g).connect(makePanner(pos.x, pos.y, pos.z)).connect(dest(c));
        o.start(t0);
        o.stop(t0 + 0.14);
      } else {
        burstAt(pos, 0.08, 2500, 1.0, 0.5, 'bandpass', delay);
      }
    } else if (sniper) {
      noiseBurst(0.38, 300, 0.7, 0.35);
      noiseBurst(0.55, 5200, 0.5, 0.15, 'highpass');
    } else noiseBurst(0.08, 2500, 1.0, 0.18, 'bandpass');
  },
  // bot muzzle: synthesized laser "pew" (downward sweep + a quinty upper
  // voice), HRTF-panned at the shooter with speed-of-sound delay — clearly
  // not a firearm, and deliberately quieter than the real gunshot samples
  laserAt(pos, dist) {
    const c = ac();
    const t0 = c.currentTime + (dist || 0) / 343;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.2, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    for (const [type, f0, f1, mix] of [
      ['sawtooth', 1500, 240, 1], ['square', 2250, 360, 0.35],
    ]) {
      const o = c.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(f0, t0);
      o.frequency.exponentialRampToValueAtTime(f1, t0 + 0.16);
      const m = c.createGain();
      m.gain.value = mix;
      o.connect(m).connect(g);
      o.start(t0);
      o.stop(t0 + 0.2);
    }
    if (pos) g.connect(makePanner(pos.x, pos.y, pos.z)).connect(dest(c));
    else g.connect(dest(c));
  },
  // bullet striking a body: real thump sample. pos → HRTF-panned at the
  // victim (the shooter and bystanders hear their bullet land, delayed and
  // dulled by distance); pos omitted → head-locked full-gain (YOU were hit)
  hitBody(pos, dist) {
    const opts = { rate: 0.92 + Math.random() * 0.16 };
    if (pos) {
      opts.delay = (dist || 0) / 343;
      opts.lowpass = Math.max(1500, 18000 - (dist || 0) * 14);
    }
    if (!playSample('hit_body', pos ? 0.9 : 0.75, pos, opts))
      noiseBurst(0.12, 300, 0.8, pos ? 0.15 : 0.3);
  },
  // supersonic near-miss: a swelling doppler hiss panned at the bullet's
  // closest-approach point — the "that one nearly got me" cue. Human rounds
  // only; bot lasers announce themselves with the zap at their impact instead.
  whiz(pos) {
    const c = ac();
    const dur = 0.22;
    const len = Math.floor(c.sampleRate * dur);
    const buf = c.createBuffer(1, len, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.sin(Math.PI * (i / len)) ** 1.5;
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 1.2;
    f.frequency.setValueAtTime(6000, c.currentTime);
    f.frequency.exponentialRampToValueAtTime(900, c.currentTime + dur);   // doppler drop
    const g = c.createGain();
    g.gain.value = 0.5;
    src.connect(f).connect(g).connect(makePanner(pos.x, pos.y, pos.z)).connect(dest(c));
    src.start();
  },
  hitConfirm() { tone(880, 0.07, 0.15, 'sine'); },
  kill() { tone(660, 0.1, 0.2); setTimeout(() => tone(990, 0.14, 0.2), 90); },
  damage() { tone(140, 0.22, 0.3, 'sawtooth', -40); },
  // station-sign "shot absorbed, nothing happened" double-beep
  uiDeny() { tone(500, 0.05, 0.12, 'square'); setTimeout(() => tone(700, 0.05, 0.12, 'square'), 140); },
  grab() { noiseBurst(0.05, 800, 0.8, 0.1); },
  // jet-suit ignition whoosh (auto-fires when the suit lights up mid-air)
  jetOn() { noiseBurst(0.5, 1200, 0.4, 0.3, 'bandpass'); tone(90, 0.4, 0.18, 'sawtooth', 160); },
  // speed in m/s; call every frame
  wind(speed, gliding) {
    if (!windGain) return;
    const t = Math.min(1, Math.max(0, (speed - 3) / 18));
    const target = t * t * (gliding ? 0.4 : 0.22);
    windGain.gain.setTargetAtTime(target, ctx.currentTime, 0.15);
    windFilter.frequency.setTargetAtTime(250 + t * 600, ctx.currentTime, 0.2);
  },
  // bike engine loop; call every frame (on=false idles it down to silence)
  engine(speed, throttle, on) {
    if (!ctx) return;
    const e = engineNodes();
    const t = ctx.currentTime;
    if (!on) { e.g.gain.setTargetAtTime(0, t, 0.08); return; }
    const rev = Math.max(0, throttle || 0);
    const hz = 46 + speed * 2.4 + rev * 26;        // idle putter → screaming descent
    e.o1.frequency.setTargetAtTime(hz, t, 0.06);
    e.o2.frequency.setTargetAtTime(hz * 0.5, t, 0.06);
    e.f.frequency.setTargetAtTime(300 + speed * 30 + rev * 250, t, 0.08);
    e.g.gain.setTargetAtTime(0.05 + 0.05 * Math.min(1, speed / 15) + 0.05 * rev, t, 0.08);
  },
  // chairlift hum loop; level 0..1 (0 = off), call every frame
  lift(level) {
    if (!ctx) return;
    const l = liftNodes();
    const k = Math.max(0, Math.min(1, level));
    l.g.gain.setTargetAtTime(k * 0.12, ctx.currentTime, 0.25);
  },
  // sheave-battery clack as the ridden chair crosses a pylon
  liftClack() {
    noiseBurst(0.05, 900, 1.2, 0.14, 'bandpass');
    setTimeout(() => noiseBurst(0.05, 700, 1.2, 0.1, 'bandpass'), 40);
  },
  // swim ambience loop; level 0..1 (the player's eased swim blend, ×lap_gain),
  // speed = horizontal m/s — strokes raise both loudness and brightness.
  // Call every frame; level 0 idles it to silence like the vehicle loops.
  swim(level, speed) {
    if (!ctx) return;
    if (level <= 0.01 && !swimN) return;
    const s = swimNodes();
    const t = ctx.currentTime;
    const k = Math.min(1, (speed || 0) / 2.5);
    const base = Math.max(0, level) * (0.05 + 0.11 * k);
    s.g.gain.setTargetAtTime(base, t, 0.25);
    s.lfoG.gain.setTargetAtTime(base * 0.55, t, 0.25);
    s.f.frequency.setTargetAtTime(420 + k * 520, t, 0.3);
  },
  // water entry / exit splash; k 0..1 = impact intensity (×splash_gain)
  splash(k) {
    const kk = Math.max(0.08, Math.min(1, k || 0));
    noiseBurst(0.22 + 0.3 * kk, 650, 0.8, 0.5 * kk);               // body plunge
    noiseBurst(0.45 + 0.4 * kk, 3000, 0.5, 0.28 * kk, 'highpass'); // spray
    tone(230 - 90 * kk, 0.2, 0.14 * kk, 'sine', -70);              // bloop
  },
  // underwater muffle: depth of the EYE below the sheet (m); <= 0 = clear.
  // Dulls the ENTIRE mix via the master bus (dest()) — every source routes
  // through it precisely so this one knob exists.
  duck(depth) {
    if (!ctx || !masterLP) return;
    masterLP.frequency.setTargetAtTime(depth > 0.02 ? 620 : 19000,
      ctx.currentTime, 0.06);
  },
  // jet-suit roar loop; intensity 0..1 (0 = off), call every frame
  jet(intensity) {
    if (!ctx) return;
    const j = jetNodes();
    const t = ctx.currentTime;
    const k = Math.max(0, Math.min(1, intensity));
    j.g.gain.setTargetAtTime(k * 0.32, t, 0.12);
    j.f.frequency.setTargetAtTime(700 + k * 1300, t, 0.15);
  },
};
